import { UploadElementType } from "@/components/common/point-cloud-file-upload-context/use-upload-element-types";
import { isTaskActive } from "@/components/ui/progress-overview/cards/task-descriptions";
import { selectBackgroundTasks } from "@/store/background-tasks/background-tasks-selector";
import { selectAreaFor } from "@/store/selections-selectors";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import {
  isMultiFileUploadTask,
  MultiFileUploadTask,
} from "@/utils/background-tasks";
import { parseVector3 } from "@faro-lotv/app-component-toolbox";
import { assert, GUID } from "@faro-lotv/foundation";
import {
  selectIElement,
  selectIElementWorldPosition,
} from "@faro-lotv/project-source";
import { useMemo } from "react";
import { Vector3 } from "three";

export type UploadingWaypoint = {
  /** Unique identifier for the upload operation. */
  id: string;

  /** The position of the waypoint in three.js world coordinates */
  worldPosition: Vector3;

  /** The names of the files being uploaded */
  files: string[];
};

/**
 * @returns the currently uploading waypoints for an area
 * @param areaId The area to filter the tasks by. Supports undefined while loading.
 */
export function useCurrentlyUploadingWaypoints(
  areaId?: GUID,
): UploadingWaypoint[] {
  const { getState } = useAppStore();

  const uploadTasksForWaypoints = useAppSelector(
    selectBackgroundTasks(
      (task): task is MultiFileUploadTask =>
        isMultiFileUploadTask(task) &&
        isTaskActive(task.state) &&
        task.metadata.uploadElementType === UploadElementType.imageData,
    ),
  );

  return useMemo(
    () =>
      uploadTasksForWaypoints
        .filter((task) => {
          const taskElement = selectIElement(task.iElementId)(getState());
          return (
            taskElement && selectAreaFor(taskElement)(getState())?.id === areaId
          );
        })
        .map((task): UploadingWaypoint => {
          assert(
            task.iElementId,
            "Expected image upload task to have an iElementId",
          );
          return {
            id: task.id,
            files: task.metadata.filenames,
            worldPosition: parseVector3(
              selectIElementWorldPosition(task.iElementId)(getState()),
            ),
          };
        }),
    [areaId, getState, uploadTasksForWaypoints],
  );
}
